import { FBMessage } from '@/types/fb-type';
import { WsEvent } from '@/types/ws-type';

type UnknownObject = Record<string, any>;

function firstUrl(...values: unknown[]): string {
  return values.find((value): value is string => typeof value === 'string' && value.trim().length > 0) ?? '';
}

// Kafka webhook attachments use payload.url; fetched API messages use media data/fileUrl.
// Empty API fields must not suppress a usable webhook URL.
export function resolveAttachmentUrl(att: UnknownObject): string {
  return firstUrl(
    att.payload?.url, att.url,
    att.imageData?.url, att.image_data?.url,
    att.fileUrl, att.file_url,
    att.audioData?.url, att.audio_data?.url,
    att.videoData?.url, att.video_data?.url,
    att.previewUrl, att.preview_url,
    att.imageData?.previewUrl, att.imageData?.preview_url,
    att.image_data?.preview_url, att.image_data?.previewUrl,
  );
}

export function mergeChatAttachments(previous: NonNullable<FBMessage['attachments']>, incoming: NonNullable<FBMessage['attachments']>) {
  if (!incoming.length) return previous;
  const usedPrevious = new Set<number>();
  const merged = incoming.map((next, index) => {
    let oldIndex = previous.findIndex((old, i) => !usedPrevious.has(i) && old.id === next.id);
    if (oldIndex < 0 && index < previous.length && !usedPrevious.has(index)) oldIndex = index;
    if (oldIndex < 0) return next;
    usedPrevious.add(oldIndex);
    const old = previous[oldIndex];
    const oldUrl = resolveAttachmentUrl(old as unknown as UnknownObject);
    if (resolveAttachmentUrl(next as unknown as UnknownObject) || !oldUrl) return next;
    return {
      ...old, ...next,
      fileUrl: oldUrl,
      imageData: old.imageData,
      image: old.image || next.image,
      audio: old.audio || next.audio,
      video: old.video || next.video,
    };
  });
  previous.forEach((old, index) => { if (!usedPrevious.has(index)) merged.push(old); });
  return merged;
}

export function normalizeFbMessage(payload: unknown): FBMessage | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const source = payload as UnknownObject;
  const rawMessage = source.message;
  const text =
    typeof rawMessage === 'string'
      ? rawMessage
      : typeof rawMessage?.text === 'string'
        ? rawMessage.text
        : '';

  const id =
    source.id ||
    rawMessage?.mid ||
    source.mid ||
    `${source.sender?.id ?? source.from?.id ?? 'unknown'}-${source.timestamp ?? Date.now()}`;

  const attachmentSource = source.attachments ?? rawMessage?.attachments;
  const rawAttachments: any[] = Array.isArray(attachmentSource)
    ? attachmentSource : Array.isArray(attachmentSource?.data) ? attachmentSource.data : [];

  const createdTime =
    typeof source.createdTime === 'string'
      ? source.createdTime
      : typeof source.created_time === 'string'
        ? source.created_time
        : source.timestamp
          ? new Date(Number(source.timestamp) < 1_000_000_000_000
              ? Number(source.timestamp) * 1000 : Number(source.timestamp)).toISOString()
          : new Date().toISOString();

  // Resolve the sticker field: the backend sets this from `sticker_id` (numeric string),
  // not a URL. The actual image URL arrives inside `message.attachments[].url`.
  // Numeric sticker ids are not URLs; use the real attachment URL.
  const rawSticker = source.sticker || rawMessage?.sticker || rawMessage?.sticker_id || '';
  const isNumericId = typeof rawSticker === 'string' && /^\d+$/.test(rawSticker);

  let resolvedSticker = '';
  if (rawSticker) {
    if (!isNumericId) {
      // Already a URL
      resolvedSticker = rawSticker;
    } else {
      // Try to extract the image URL from the first attachment
      const firstAtt = rawAttachments[0] as Record<string, any> | undefined;
      const attUrl = firstAtt ? resolveAttachmentUrl(firstAtt) : '';
      if (attUrl) {
        resolvedSticker = attUrl;
      }
    }
  }

  const seenAttachmentKeys = new Set<string>();
  const normalizedAttachments = rawAttachments
    .map((att: any, index: number) => {
      if (!att || typeof att !== 'object') return att;

      const url = resolveAttachmentUrl(att);

      const type = (att.type ?? '').toLowerCase();

      const isImage =
        type === 'image' ||
        type === 'photo' ||
        (type === 'fallback' && !!rawSticker) ||
        att.image === true ||
        /^image\//i.test(att.mimeType || att.mime_type || '') ||
        !!att.imageData?.url ||
        !!att.image_data?.url ||
        /\.(heic|heif|jpg|jpeg|png|webp|gif|svg)($|\?)/i.test(url);

      const isVoiceClip = /audioclip|voice[-_]/i.test(`${att.name ?? ''} ${url}`);

      const isAudio =
        !isImage &&
        (isVoiceClip ||
          type === 'audio' ||
          type === 'voice' ||
          att.audio === true ||
          !!att.audioData ||
          !!att.audio_data ||
          /\.(mp3|wav|webm|ogg|m4a|aac|opus)($|\?)/i.test(url));

      const isVideo =
        !isImage &&
        !isAudio &&
        (type === 'video' ||
          att.video === true ||
          !!att.videoData ||
          !!att.video_data ||
          /\.(mp4|mov|avi|wmv|flv|mkv|webm)($|\?)/i.test(url));

      let mimeType = att.mimeType || att.mime_type || '';
      if (!mimeType) {
        if (isImage) mimeType = 'image/jpeg';
        else if (isAudio) mimeType = 'audio/mp4';
        else if (isVideo) mimeType = 'video/mp4';
      }

      let imageData = att.imageData ?? att.image_data;
      if (isImage && url) {
        imageData = {
          ...imageData,
          url,
          previewUrl: firstUrl(att.payload?.previewUrl, att.payload?.preview_url,
            att.previewUrl, att.preview_url, imageData?.previewUrl, imageData?.preview_url, url),
          renderAsSticker: imageData?.renderAsSticker ?? imageData?.render_as_sticker ?? !!rawSticker,
        };
      }

      return {
        ...att,
        id: att.id || `${id}-att-${index}`,
        mimeType,
        image: isImage,
        video: isVideo,
        audio: isAudio,
        fileUrl: url,
        imageData,
        size: att.size ?? 0,
      };
    })
    .filter((att: any) => {
      if (!att || typeof att !== 'object') return false;
      const key = att.fileUrl || att.url || att.imageData?.url || att.id;
      if (key) {
        if (seenAttachmentKeys.has(key)) return false;
        seenAttachmentKeys.add(key);
      }
      return true;
    });

  const normalized = {
    ...source,
    id: String(id),
    createdTime,
    message: text,
    from: source.from?.id ? source.from : (source.sender ?? source.from ?? { id: '', name: '' }),
    to: source.to ?? (source.recipient ? [source.recipient] : []),
    sticker: resolvedSticker,
    attachments: normalizedAttachments,
  };

  return Object.assign(new FBMessage(), normalized);
}

export function parseMediaReadyEvent(messageBody: string): { conversationId: string } | null {
  try {
    const parsed = JSON.parse(messageBody) as UnknownObject;
    if (parsed?.type === 'MEDIA_READY' && typeof parsed.payload?.conversationId === 'string') {
      return { conversationId: parsed.payload.conversationId };
    }
  } catch {
    // not JSON — not a control event
  }
  return null;
}

/** Keep the sent photo visible while Facebook finishes indexing its media URL. */
export function createSentImageMessage(
  response: unknown,
  { previewUrl, mimeType, size, pageId }: { previewUrl: string; mimeType: string; size: number; pageId: string },
): FBMessage {
  const source = response && typeof response === 'object' ? response as UnknownObject : {};
  const message = normalizeFbMessage({
    ...source,
    id: source.id || source.mid || `temp-img-${Date.now()}`,
    from: source.from?.id ? source.from : { id: pageId, name: 'Page', username: '' },
  })!;
  const attachment = message.attachments?.[0];
  const url = attachment ? resolveAttachmentUrl(attachment) || previewUrl : previewUrl;
  message.attachments = [{
    ...attachment,
    id: attachment?.id || `${message.id}-att-0`,
    mimeType: mimeType || 'image/jpeg',
    size,
    image: true,
    video: false,
    audio: false,
    fileUrl: url,
    imageData: {
      width: 0, height: 0, maxWidth: 0, maxHeight: 0, renderAsSticker: false,
      ...attachment?.imageData,
      url,
      previewUrl: attachment?.imageData?.previewUrl || url,
    },
  }];
  return message;
}

export function mapWebsocketEventToFbMessage(messageBody: string): FBMessage | null {
  const parsed = JSON.parse(messageBody) as WsEvent | UnknownObject;
  if ('type' in parsed && parsed.type === 'MEDIA_READY') return null;
  const payload =
    'type' in parsed && parsed.type === 'NEW_MESSAGE'
      ? (parsed as WsEvent).payload
      : parsed;

  return normalizeFbMessage(payload);
}
